import {
  FC, useEffect, useMemo, useState, 
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Mixpanel } from 'mixpanel';
import useUser from 'api/hooks/useUser';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Select';
import { BriefInitialProps } from 'features/ApplicationBrief';
import { BriefProps, SelectProps } from 'utils/types';

interface IData {
  companyName: string;
}
interface Props {
  briefData: BriefProps[];
  setProductAdd: (value: BriefInitialProps) => void;
  setIsProductAdd: (value: boolean) => void;
  setGeneretedBrief: (value: boolean) => void;
  setComparisonData: (value: null) => void;
}

const ApplicationIndustryInitial: FC<Props> = ({
  setProductAdd,
  briefData,
  setIsProductAdd,
  setGeneretedBrief,
  setComparisonData,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { control, watch } = useForm<IData>();
  const watchCompanyName = watch(['companyName']);
  const [industry, setIndustry] = useState<SelectProps | null>(null);
  const [subIndustry, setSubIndustry] = useState<SelectProps | null>(null);
  const [apps, setApps] = useState<SelectProps | null>(null);
  const [products, setProducts] = useState<SelectProps | null>(null);
  const { data: user } = useUser();

  const onSubmit = (value: boolean) => {
    if (industry?.value && apps?.value && products?.value) {
      const res = {
        watchCompanyName: watchCompanyName?.[0],
        industry: industry?.value,
        subIndustry: subIndustry?.value,
        apps: apps?.value,
        products: products?.value,
      };
      Mixpanel.track('Application Brief Product Selection', {
        role: user?.role?.name,
        organization: user?.organization,
        products: res?.products,
        application: res?.apps,
      });
      setProductAdd(res);

      if (value) {
        setGeneretedBrief(true);
      } else {
        setIsProductAdd(true);
      }
    }
  };
  const industrySelect = useMemo(() => {
    const result: string[] = [];
    briefData?.forEach((brief) => {
      result.push(brief?.Industry);
    });
    const unique = [...new Set(result)];

    return unique.map((uniqBrief) => ({ value: uniqBrief,
      label: uniqBrief }));
  }, [briefData]);

  const emptySubIndustryOption = useMemo(() => t('noSubSegment'), [language]);

  const getSubIndustryList = useMemo(() => {
    const result: string[] = [];

    if (industry?.value) {
      briefData?.forEach((briefItem: BriefProps) => {
        if (briefItem?.Industry === industry?.value) {
          if (briefItem['Sub-Industry']) {
            result.push(briefItem['Sub-Industry']);
          } else {
            if (!result.includes(emptySubIndustryOption)) {
              result.push(emptySubIndustryOption);
            }
          }
        }
      });
    }

    const unique = [...new Set(result)];

    return unique.map((subIndustryItem) => ({
      value: subIndustryItem,
      label: subIndustryItem,
    }));
  }, [industry, emptySubIndustryOption]);

  const getApplicationList = useMemo(() => {
    const result: string[] = [];

    if (industry?.value) {
      briefData?.forEach((briefItem: BriefProps) => {
        if (subIndustry?.value) {
          if (
            briefItem?.Industry === industry?.value &&
            (briefItem?.['Sub-Industry'] === subIndustry?.value ||
              subIndustry?.value === emptySubIndustryOption ||
              briefItem?.['Sub-Industry'] === null)
          ) {
            result.push(briefItem?.Application);
          }
        } else {
          if (briefItem?.Industry === industry?.value) {
            result.push(briefItem?.Application);
          }
        }
      });
    }

    const unique = [...new Set(result)];

    return unique.map((application) => ({
      value: application,
      label: application,
    }));
  }, [industry, subIndustry, emptySubIndustryOption]);

  const getProductsList = useMemo(() => {
    const result: string[] = [];

    if (industry?.value) {
      briefData?.forEach((briefItem: BriefProps) => {
        if (subIndustry?.value) {
          if (
            briefItem?.Industry === industry?.value &&
            (briefItem?.['Sub-Industry'] === subIndustry?.value ||
              subIndustry?.value === `${t('no')} ${t('industrySubSegment')}` ||
              briefItem?.['Sub-Industry'] === null) &&
            briefItem?.Application === apps?.value
          ) {
            result.push(briefItem['Product recommendation']);
          }
        } else {
          if (
            briefItem?.Industry === industry?.value &&
            briefItem?.Application === apps?.value
          ) {
            result.push(briefItem['Product recommendation']);
          }
        }
      });
    }

    const unique = [...new Set(result)];

    return unique.map((productItem) => ({
      value: productItem,
      label: productItem,
    }));
  }, [industry, subIndustry, apps, emptySubIndustryOption]);

  useEffect(() => {
    if (
      getSubIndustryList?.length === 1 &&
      getSubIndustryList?.[0]?.value !== null
    ) {
      setSubIndustry(getSubIndustryList[0]);
    }
  }, [industry]);

  useEffect(() => {
    if (
      getApplicationList?.length === 1 &&
      getApplicationList?.[0]?.value !== null
    ) {
      setApps(getApplicationList[0]);
    }
  }, [subIndustry, industry]);

  useEffect(() => {
    if (getProductsList?.length && getProductsList?.[0]?.value !== null) {
      setProducts(getProductsList[0]);
    }
  }, [apps]);

  useEffect(() => {
    if (products) {
      setComparisonData(null);
    }
  }, [products]);

  const disabledState = useMemo(() => {
    if (industry?.value && apps?.value && !!products?.value) {
      return false;
    }

    return true;
  }, [watchCompanyName]);

  return (
    <form className="border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl">
      <div className="grid gap-6 grid-cols-2 grid-rows-2 md:grid-cols-1 md:gap-2">
        <div className="w-full mb-1 md:mb-4">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <Input
                titleClassName="top-[-9px] left-[8px]"
                className="w-full"
                title={t('companyName')}
                placeholder={t('enterCompanyName')}
                onChange={onChange}
                type="text"
                maxLength={40}
              />
            )}
            name="companyName"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 md:hidden"></div>
        <div className="w-full mb-1 md:mb-4">
          <Select
            options={industrySelect}
            placeholder={t('selectValue')}
            title={t('industry')}
            titleClassName="top-[-9px] left-[8px]"
            value={[industry!]}
            onChange={(values) => {
              setIndustry(values);
              setSubIndustry(null);
              setApps(null);
              setProducts(null);
            }}
          />
        </div>
        <div className="w-full mb-1 md:mb-4">
          <Select
            disabled={!industry?.value}
            options={getSubIndustryList}
            value={
              subIndustry?.value
                ? [
                  {
                    value: subIndustry.value,
                    label: subIndustry.value,
                  },
                ]
                : []
            }
            placeholder={t('selectValue')}
            title={t('industrySubSegment')}
            titleClassName="top-[-9px] left-[8px]"
            onChange={(values) => {
              setSubIndustry(values);
              setApps(null);
              setProducts(null);
            }}
          />
        </div>
        <div className="w-full mb-1 md:mb-4">
          <Select
            disabled={
              !industry?.value ||
              (getSubIndustryList?.[0]?.value !== null
                ? !subIndustry?.value
                : false)
            }
            value={
              apps?.value
                ? [
                  {
                    value: apps.value,
                    label: apps.value,
                  },
                ]
                : []
            }
            options={getApplicationList}
            titleClassName="top-[-9px] left-[8px]"
            placeholder={t('selectValue')}
            title={t('applications')}
            onChange={(values) => {
              setApps(values);
              setProducts(null);
            }}
          />
        </div>
        <div className="w-full mb-1 md:mb-4">
          <Select
            disabled={
              !industry?.value ||
              (getSubIndustryList?.[0]?.value !== null &&
                !subIndustry?.value) ||
              !apps?.value
            }
            value={
              products?.value
                ? [
                  {
                    value: products.value,
                    label: products.value,
                  },
                ]
                : []
            }
            titleClassName="top-[-9px] left-[8px]"
            options={getProductsList}
            placeholder={t('selectValue')}
            title={t('products')}
            onChange={(values) => setProducts(values)}
          />
        </div>
      </div>
      <div className="grid gap-6 grid-cols-2 grid-rows-1 md:grid-cols-1 md:gap-2 mt-8">
        <div className="flex justify-end md:justify-center">
          <Button
            onClick={() => onSubmit(false)}
            disabled={disabledState}
            btnType="secondary"
          >
            {t('addProductFigures')}
          </Button>
        </div>
        <div className="md:justify-center md:flex md:mt-1">
          <Button
            disabled={disabledState}
            onClick={() => onSubmit(true)}
            className="w-[220px]"
          >
            {t('generateBrief')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ApplicationIndustryInitial;
